import OfflineStore from "./OfflineStore";
import PrecacheIndex from "./PrecacheIndex";
import LiveApiClient from "../api/LiveApiClient";

/**
 * PageItemsCache — on-demand fetcher for page→item ID mappings.
 *
 * 3-tier cache:
 *   1. Memory (Map) — instant, cleared on delta sync or destroy
 *   2. IDB (OfflineStore sync metadata) — persists across sessions
 *   3. API (Catalog_Page/{id}/items) — authoritative source
 *
 * Usage:
 *   const itemIds = await PageItemsCache.getPageItems(pageId, catalogId, accountCode, apiKey);
 *   const isCached = PageItemsCache.hasCachedPageItems(pageId);
 */
export default class PageItemsCache {
	static #memory = new Map();   // pageId → [itemIds]
	static #inflight = new Map(); // pageId → Promise (dedup concurrent requests)

	/**
	 * Get ordered item IDs for a page. Checks memory → IDB → API.
	 * Populates PrecacheIndex.pageIndex on cache miss.
	 *
	 * @param {string|number} pageId
	 * @param {string|number} catalogId
	 * @param {string} accountCode
	 * @param {string} apiKey
	 * @returns {Promise<number[]|null>} Ordered item IDs, or null on failure
	 */
	static async getPageItems(pageId, catalogId, accountCode, apiKey) {
		const key = String(pageId);

		// 1. Memory cache
		if (this.#memory.has(key)) {
			return this.#memory.get(key);
		}

		// 2. Dedup concurrent requests for the same page
		if (this.#inflight.has(key)) {
			return this.#inflight.get(key);
		}

		const promise = this.#fetchAndCache(key, pageId, catalogId, accountCode, apiKey);
		this.#inflight.set(key, promise);

		try {
			return await promise;
		} finally {
			this.#inflight.delete(key);
		}
	}

	static async #fetchAndCache(key, pageId, catalogId, accountCode, apiKey) {
		// 2. IDB cache
		try {
			const store = OfflineStore.ForCatalog(catalogId);
			const cached = await store.GetSyncMetadata(`pageItems:${pageId}`);
			if (cached && Array.isArray(cached)) {
				this.#memory.set(key, cached);
				// Populate PrecacheIndex
				const index = PrecacheIndex.ForCatalog(catalogId);
				if (index) {
					index.setPageItems(pageId, cached);
				}
				return cached;
			}
		} catch (e) {
			// IDB read failed — continue to API
		}

		// 3. API fetch
		try {
			const response = await LiveApiClient.get(`Catalog_Page/${pageId}/items`, {
				headers: {
					'api-key': apiKey,
					account: accountCode,
				},
			});

			const items = response.data?.items;
			if (!Array.isArray(items)) {
				return null;
			}

			const itemIds = items.map(Number);

			// Cache in memory
			this.#memory.set(key, itemIds);

			// Cache in IDB (fire and forget)
			try {
				const store = OfflineStore.ForCatalog(catalogId);
				store.SetSyncMetadata(`pageItems:${pageId}`, itemIds);
			} catch (e) {
				// Non-fatal
			}

			// Populate PrecacheIndex
			const index = PrecacheIndex.ForCatalog(catalogId);
			if (index) {
				index.setPageItems(pageId, itemIds);
			}

			return itemIds;
		} catch (e) {
			// API fetch failed (offline, network error, etc.)
			return null;
		}
	}

	/**
	 * Synchronous check: is this page's item list in memory cache?
	 * Used by OfflineInterceptor to gate precache serving without async overhead.
	 *
	 * @param {string|number} pageId
	 * @returns {boolean}
	 */
	static hasCachedPageItems(pageId) {
		return this.#memory.has(String(pageId));
	}

	/**
	 * Clear all cached page items (memory only).
	 * Called after delta sync completes or on catalog switch.
	 * IDB entries are left in place — they'll be overwritten on next fetch.
	 */
	static invalidateAll() {
		this.#memory.clear();
		this.#inflight.clear();
	}

	/**
	 * Clear a single page from memory cache.
	 * @param {string|number} pageId
	 */
	static invalidatePage(pageId) {
		this.#memory.delete(String(pageId));
	}
}
